import { LineSeparator } from '@/components/LineSeparator';
import { Loading } from '@/components/Loading';
import { useRole } from '@/hooks/UseRole';
import { UpdateUserAvatar } from '@/partials/dashboard/Users/UpdateUser/UpdateUserAvatar';
import { UpdateUserDelete } from '@/partials/dashboard/Users/UpdateUser/UpdateUserDelete';
import { UpdateUserInformations } from '@/partials/dashboard/Users/UpdateUser/UpdateUserInformations';
import { useUserStore } from '@/stores/useUserStore';
import { motion } from 'framer-motion';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      duration: 0.3,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3 },
  },
};

export const UpdateUser = () => {
  const { id } = useParams<{ id: string }>();
  const isAdmin = useRole('admin');
  const { user, showUser, loading } = useUserStore();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      showUser(Number(id));
    }
  }, [id, showUser]);

  const handleBack = () => {
    navigate(-1);
  };

  if (!user || loading) return <Loading />;

  return (
    <div className="h-full space-y-6 overflow-y-auto">
      {/* Header */}
      <motion.div
        className="flex flex-col gap-4 p-4"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header avec bouton retour */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100"
          >
            <FaArrowLeft className="size-4" />
            {t('common:back')}
          </button>
          <div className="h-6 w-px bg-gray-300" />
          <h1 className="text-lg font-semibold text-secondary">
            {t('updateUser:title', {
              name: `${user.firstname} ${user.lastname}`,
            })}
          </h1>
        </div>
      </motion.div>

      {/* Contenu */}
      <motion.div
        className="px-4 pb-4"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={itemVariants}>
          <UpdateUserAvatar />
        </motion.div>

        <motion.div variants={itemVariants}>
          <LineSeparator className="my-8" />
        </motion.div>

        <motion.div variants={itemVariants}>
          <UpdateUserInformations />
        </motion.div>

        {isAdmin && (
          <>
            <motion.div variants={itemVariants}>
              <LineSeparator className="my-8" />
            </motion.div>
            <motion.div variants={itemVariants}>
              <UpdateUserDelete />
            </motion.div>
          </>
        )}
      </motion.div>
    </div>
  );
};
